import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import '../../components/public/Public.css';
import './Register.css';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { showToast } = useToast();

  const [email, setEmail]   = useState(params.get('email') || '');
  const [firstname, setFirstname] = useState(params.get('name') || '');
  const [otp, setOtp]       = useState('');
  const [sent, setSent]     = useState(false);
  const [busy, setBusy]     = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendCode = useCallback(async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setErrors({ email:'Enter a valid email.' }); return; }
    if (!firstname.trim()) { setErrors({ firstname:'Enter your first name.' }); return; }
    setErrors({});
    setBusy(true);
    try {
      await api.post('/public/send-otp', { email, firstname: firstname.trim() });
      setSent(true);
      setResendIn(45);
      showToast('Verification code sent.', 'success');
    } catch {
      showToast('Failed to send code.', 'error');
    } finally { setBusy(false); }
  }, [email, firstname, showToast]);

  const verify = async () => {
    if (!/^\d{6}$/.test(otp)) { setErrors({ otp:'Enter the 6-digit code.' }); return; }
    setErrors({});
    setBusy(true);
    try {
      await api.post('/public/verify-otp', { email, otp });
      showToast('Email verified successfully!', 'success');
      navigate('/');
    } catch (err) {
      setErrors({ otp: err.response?.data?.error || 'Invalid or expired code.' });
    } finally { setBusy(false); }
  };

  return (
    <div className="pub-page">
      <Navbar />
      <div className="reg-wrap">
        <div className="reg-container" style={{ maxWidth: 520 }}>
          <div className="reg-card">
            <div className="reg-pane">
              <h2>Verify your email</h2>
              <p className="reg-sub">
                {sent ? `Enter the 6-digit code we sent to ${email}.` : 'Confirm your email to receive a verification code.'}
              </p>

              {!sent ? (
                <>
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input className={`form-control ${errors.firstname ? 'is-invalid' : ''}`} value={firstname} onChange={e => setFirstname(e.target.value)} />
                    {errors.firstname && <span className="invalid-feedback">{errors.firstname}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input className={`form-control ${errors.email ? 'is-invalid' : ''}`} value={email} onChange={e => setEmail(e.target.value)} />
                    {errors.email && <span className="invalid-feedback">{errors.email}</span>}
                  </div>
                  <button className="pub-btn pub-btn-primary reg-btn" style={{width:'100%'}} onClick={sendCode} disabled={busy}>
                    {busy ? <span className="spinner spinner-sm" /> : 'Send Code'}
                  </button>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Verification Code</label>
                    <input
                      className={`form-control form-control-lg reg-otp-input ${errors.otp ? 'is-invalid' : ''}`}
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                      onKeyDown={e => e.key === 'Enter' && verify()}
                      placeholder="------"
                      maxLength={6}
                      inputMode="numeric"
                      autoFocus
                    />
                    {errors.otp && <span className="invalid-feedback">{errors.otp}</span>}
                  </div>
                  <div className="reg-resend">
                    {resendIn > 0
                      ? <span className="text-muted">Resend code in {resendIn}s</span>
                      : <button className="reg-link-btn" onClick={sendCode} disabled={busy}>Resend code</button>}
                  </div>
                  <button className="pub-btn pub-btn-primary reg-btn" style={{width:'100%', marginTop:'1rem'}} onClick={verify} disabled={busy}>
                    {busy ? <span className="spinner spinner-sm" /> : 'Verify Email'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
